import React, { useState, useRef, useEffect } from 'react';
import styles from '../Vol1.module.css';
import { shuffle } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { getAudioUrl, getImageUrl } from '../services/r2Service';
import { FaPlay, FaPause, FaForward, FaBackward, FaRandom, FaRedo } from 'react-icons/fa';

interface Song {
  audioFile: string;
  coverImage: string;
  title: string;
}

interface Vol3Props {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

const songs: Song[] = [
  { 
    audioFile: getAudioUrl('megapackv3/bounce 08.06.23.mp3'),
    coverImage: getImageUrl('bounce.png'),
    title: 'Bounce' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/groove 02.20.21.mp3'),
    coverImage: getImageUrl('groove.png'),
    title: 'Groove' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/ice 10.12.20_2.mp3'),
    coverImage: getImageUrl('ice.png'),
    title: 'Ice' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/rrollin 05.02.21_4.mp3'),
    coverImage: getImageUrl('rollin.png'),
    title: 'Rollin' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/Save The Day 07.27.23_2.mp3'),
    coverImage: getImageUrl('savetheday.png'),
    title: 'Save The Day' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/Truth Teller 08.27.23_2.mp3'),
    coverImage: getImageUrl('truthteller.png'),
    title: 'Truth Teller' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/village 03.30.21_2.mp3'),
    coverImage: getImageUrl('village.png'),
    title: 'Village' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/Closed Doors 05.08.25.mp3'),
    coverImage: getImageUrl('java.png'),
    title: 'Closed Doors' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/cold in my room 05.19.20.mp3'),
    coverImage: getImageUrl('coldinmyroom.png'),
    title: 'Cold in My Room' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/daylight 09.29.mp3'),
    coverImage: getImageUrl('daylight.png'),
    title: 'Daylight' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/dum sesh 02.08.21.mp3'),
    coverImage: getImageUrl('dumsesh.png'),
    title: 'Dum Sesh' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/gravity 09.27.20.mp3'),
    coverImage: getImageUrl('gravity.png'),
    title: 'Gravity' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/signal 03.03.23.mp3'),
    coverImage: getImageUrl('signal.png'),
    title: 'Signal' 
  },
  { 
    audioFile: getAudioUrl('megapackv3/sunrise 03.17.23.mp3'),
    coverImage: getImageUrl('sunrise.png'),
    title: 'Sunrise' 
  }
];

const Vol3: React.FC<Vol3Props> = ({ searchTerm, setSearchTerm }) => {
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const navigate = useNavigate();

  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [filteredSongs, setFilteredSongs] = useState(songs);
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [shuffledSongs, setShuffledSongs] = useState(songs);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const prevImageUrlRef = useRef<string | null>(null);

  const handleNextTapeClick = () => navigate('/Vol1');
  const handlePrevTapeClick = () => navigate('/Vol2');

  const handleShuffle = () => {
    const audioElement = audioRef.current;
    const currentSong = shuffleEnabled ? shuffledSongs[currentSongIndex] : filteredSongs[currentSongIndex];
    const currentPlaybackTime = audioElement.currentTime;
    const wasPlaying = isPlaying;
    const currentSrc = audioElement.src;

    if (shuffleEnabled) {
      // When turning shuffle off, find the current song in the filtered list
      const newIndex = filteredSongs.findIndex(song => song.title === currentSong.title);
      setShuffledSongs(filteredSongs);
      setCurrentSongIndex(newIndex);
    } else {
      // When turning shuffle on, keep current song first, shuffle the rest
      const otherSongs = filteredSongs.filter(song => song.title !== currentSong.title);
      const shuffledOthers = shuffle(otherSongs);
      setShuffledSongs([currentSong, ...shuffledOthers]);
      setCurrentSongIndex(0);
    }

    // Update shuffle state
    setShuffleEnabled(!shuffleEnabled);
    setRepeat(false);

    // Only change the source if it's different
    if (currentSrc !== currentSong.audioFile) {
      audioElement.src = currentSong.audioFile;
      audioElement.load(); // Ensure the new source is loaded
    }
    
    // Restore playback position and state
    requestAnimationFrame(() => {
      audioElement.currentTime = currentPlaybackTime;
      if (wasPlaying) {
        audioElement.play().catch(() => {});
      }
    });
  };

  const handleRepeat = () => {
    const audioElement = audioRef.current;
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentPlaybackTime = audioElement.currentTime;
    const wasPlaying = isPlaying;
    const currentSrc = audioElement.src;

    setRepeat(!repeat);
    setShuffleEnabled(false);
    setShuffledSongs(filteredSongs);
    
    // Find the current song's position in the filtered list
    const newIndex = filteredSongs.findIndex(song => song.title === currentSong.title);
    setCurrentSongIndex(newIndex);

    // Only change the source if it's different
    if (currentSrc !== currentSong.audioFile) {
      audioElement.src = currentSong.audioFile;
      audioElement.load(); // Ensure the new source is loaded
    }
    
    // Restore playback position and state
    requestAnimationFrame(() => {
      audioElement.currentTime = currentPlaybackTime;
      if (wasPlaying) {
        audioElement.play().catch(() => {});
      }
    });
  };

  const handleSongEnd = () => {
    if (repeat) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
    } else {
        const nextIndex = (currentSongIndex + 1) % (shuffleEnabled ? shuffledSongs.length : filteredSongs.length);
        setCurrentSongIndex(nextIndex);
    }
  };

  useEffect(() => {
    audioRef.current.addEventListener('ended', handleSongEnd);
    return () => {
      audioRef.current.removeEventListener('ended', handleSongEnd);
    };
  }, [repeat, shuffleEnabled, currentSongIndex]);

  useEffect(() => {
    const audioElement = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, []);

  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const audioElement = audioRef.current;

    if (currentSongs.length > 0 && audioElement.src !== currentSongs[currentSongIndex].audioFile) {
        audioElement.src = currentSongs[currentSongIndex].audioFile;
        if (!shuffleEnabled) {
            audioElement.currentTime = 0;
        }
        if (isPlaying) {
            audioElement.play().catch(() => {});
        }
    } else if (currentSongs.length === 0) {
        // Stop playback and reset when no songs are available
        audioElement.pause();
        audioElement.currentTime = 0;
        setIsPlaying(false);
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredSongs(songs);
      setShuffledSongs(songs);
    } else {
      const filtered = songs.filter(song => song.title.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredSongs(filtered);
      setShuffledSongs(filtered);
    }
    setCurrentSongIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    };
  }, []);

  const formatTime = (time: number) => {
    const floorTime = Math.floor(time);
    const minutes = Math.floor(floorTime / 60);
    const seconds = '0' + (floorTime - minutes * 60);
    return minutes + ':' + seconds.substr(-2);
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const changeSong = (delta: number) => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const nextIndex = (currentSongIndex + delta + currentSongs.length) % currentSongs.length;
    setCurrentSongIndex(nextIndex);
  };

  const selectSong = (title: string) => {
    const index = filteredSongs.findIndex(song => song.title === title);
    if (index > -1) {
      setCurrentSongIndex(index);
      setIsPlaying(true);
      audioRef.current.play().catch(() => {});
    }
  };

  useEffect(() => {
    const currentSongs = shuffleEnabled ? shuffledSongs : filteredSongs;
    const currentSong = currentSongs[currentSongIndex];
    const currentImageUrl = currentSong?.coverImage;

    if (prevImageUrlRef.current !== currentImageUrl) {
      setIsImageLoading(true);
      prevImageUrlRef.current = currentImageUrl;
    }
  }, [currentSongIndex, shuffleEnabled, filteredSongs, shuffledSongs]);

  return (
    <div className={styles.container}>
      <h1 className={styles.volumeTitle}>Volume 3</h1>
      <input
        className={styles['search-bar']}
        type='text'
        placeholder='Search for a song...'
        value={searchTerm}
        onChange={event => setSearchTerm(event.target.value)}
      />
      <div className={styles.albumCoverContainer}>
        {filteredSongs.length > 0 ? (
          <img 
            src={shuffleEnabled ? shuffledSongs[currentSongIndex]?.coverImage : filteredSongs[currentSongIndex]?.coverImage} 
            alt='Cover' 
            className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
            onLoad={() => setIsImageLoading(false)}
          />
        ) : (
          <img 
            src={getImageUrl('nophoto.jpg')} 
            alt='No Results' 
            className={styles.albumCover}
          />
        )}
      </div>

      <div className={styles.audioControls}>
        <button onClick={handleShuffle} className={styles.button} title={shuffleEnabled ? 'Disable Shuffle' : 'Enable Shuffle'} disabled={filteredSongs.length === 0}>
          <FaRandom className={shuffleEnabled ? styles.activeIcon : styles.icon} />
        </button>
        <button onClick={() => changeSong(-1)} className={styles.button} title="Previous" disabled={filteredSongs.length === 0}>
          <FaBackward className={styles.icon} />
        </button>
        <button onClick={handleTogglePlay} className={`${styles.button} ${styles.playButton}`} title={isPlaying ? 'Pause' : 'Play'} disabled={filteredSongs.length === 0}>
          {isPlaying ? <FaPause className={styles.icon} /> : <FaPlay className={styles.icon} />}
        </button>
        <button onClick={() => changeSong(1)} className={styles.button} title="Next" disabled={filteredSongs.length === 0}>
          <FaForward className={styles.icon} />
        </button>
        <button onClick={handleRepeat} className={styles.button} title={repeat ? 'Disable Repeat' : 'Enable Repeat'} disabled={filteredSongs.length === 0}>
          <FaRedo className={repeat ? styles.activeIcon : styles.icon} />
        </button>
      </div>

      <div className={styles.playerControls}>
        <div className={styles.trackTitle}>
          {filteredSongs.length > 0 
            ? (shuffleEnabled ? shuffledSongs[currentSongIndex]?.title : filteredSongs[currentSongIndex]?.title)
            : 'No songs found'}
        </div>
        <div className={styles.timeControls}>
          <div className={styles.timeRow}>
            <span className={styles.timeText}>{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleTimeChange}
              className={styles.timeSlider}
              disabled={filteredSongs.length === 0}
            />
            <span className={styles.timeTextRight}>{formatTime(duration)}</span>
          </div>
        </div>
        <div className={styles.volumeSliderWrapper}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className={styles.volumeSlider}
          />
        </div>
        <div className={styles.volumeText}>Volume</div>
        <div style={{ height: '2rem' }} />
        <div className={styles.tapeControls}>
          <button onClick={handleNextTapeClick}>Next Tape</button>
          <button onClick={handlePrevTapeClick}>Previous Tape</button>
        </div>
      </div>

      <div className={styles.tracklist}>
        <h2 className={styles.tracklistLabel}>Tracklist</h2>
        {filteredSongs.length > 0 ? (
          filteredSongs.map(song => (
            <div key={song.title} className={styles.tracklistItem} onClick={() => selectSong(song.title)}>
              {song.title}
            </div>
          ))
        ) : (
          <p className={styles.tracklistLabel}>No songs match your search.</p>
        )}
      </div>
    </div>
  );
};

export default Vol3;
